import React, { useState, useContext, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { FiUser, FiLock, FiCheckCircle, FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import AuthContext from '../context/AuthContext';

const AcceptInvitation = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useContext(AuthContext);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast.error("Jeton d'invitation manquant ou invalide.");
      navigate('/login');
    }
  }, [token, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { username, password, confirmPassword } = formData;

    if (!username || !password || !confirmPassword) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/team/accept-invitation', {
        token,
        username,
        password,
      });

      // Le backend renvoie un token JWT, on l'utilise pour connecter l'utilisateur
      if (loginWithToken && response.data.token) {
        loginWithToken(response.data.token);
      } else if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }

      const displayUsername = response.data.user?.username || username;
      toast.success(`Bienvenue, ${displayUsername} !`);
      navigate('/dashboard');

    } catch (error) {
      console.error("Erreur lors de la finalisation:", error);
      toast.error(error.response?.data?.message || "Une erreur est survenue. Le lien a peut-être expiré.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold font-poppins text-gray-800">Finaliser votre inscription</h2>
          <p className="text-gray-500 mt-2">Choisissez vos identifiants pour rejoindre la boutique.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-lg">
          <div className="mb-4 relative">
            <FiUser className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
            <input id="username" type="text" placeholder="Nom d'utilisateur" value={formData.username} onChange={handleChange} className="w-full bg-gray-50 border-2 border-gray-200 rounded-lg py-3 pl-10 pr-3 text-gray-700 leading-tight focus:outline-none focus:border-brand-blue" />
          </div>
          <div className="mb-4 relative">
            <FiLock className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
            <input id="password" type={showPassword ? "text" : "password"} placeholder="Mot de passe" value={formData.password} onChange={handleChange} className="w-full bg-gray-50 border-2 border-gray-200 rounded-lg py-3 pl-10 pr-10 text-gray-700 leading-tight focus:outline-none focus:border-brand-blue" />
            <div className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <FiEyeOff className="text-gray-500" /> : <FiEye className="text-gray-500" />}
            </div>
          </div>
          <div className="mb-6 relative">
            <FiLock className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
            <input id="confirmPassword" type={showPassword ? "text" : "password"} placeholder="Confirmer le mot de passe" value={formData.confirmPassword} onChange={handleChange} className="w-full bg-gray-50 border-2 border-gray-200 rounded-lg py-3 pl-10 pr-10 text-gray-700 leading-tight focus:outline-none focus:border-brand-blue" />
          </div>

          <div className="mb-6">
            <button disabled={loading} className="bg-brand-green hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg w-full transition-all duration-300 flex items-center justify-center shadow-lg" type="submit">
              <FiCheckCircle className="mr-2" />
              {loading ? 'Finalisation...' : 'Rejoindre la boutique'}
            </button>
          </div>
          <p className="text-center text-gray-500 text-sm">
            Déjà un compte ? <Link to="/login" className="font-medium text-brand-blue hover:text-blue-800">Se connecter</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvitation;